import ExtendedBadge from '@/components/ExtendedBadge';

/**
 * Pick a badge colour from a backend status string. Backend statuses arrive in
 * several shapes (`PENDING`, `Pending`, `FINAL_REVIEW_REJECTED`, `Paid`), so the
 * match is case-insensitive and substring-based — a rejection must never be
 * painted green.
 */
function statusVariant(status: string): 'primary' | 'success' | 'warning' | 'danger' {
    if (/reject|declin|fail|cancel|expire/i.test(status)) return 'danger';
    if (/approv|paid|pass|complet|success|issued|active/i.test(status)) return 'success';
    if (/pending|progress|review|processing|draft|submitted|assign/i.test(status)) return 'warning';
    return 'primary';
}


/**
 * Every optional field below is rendered only when the application actually
 * carries it. A row is never printed with a placeholder: an applicant reading
 * a column of "N/A" cannot tell a missing answer from a broken screen.
 */
interface PaymentCardProps {
    /** Bill reference / control number — absent until a bill is raised. */
    reference?: string;
    /** Licence sub-category fees; the investment application has no such breakdown. */
    baseFee?: string;
    managementFee?: string;
    inspectionFee?: string;
    totalFees?: string;
    /** Absent until the bill is actually paid. */
    paymentDate?: string;
    /** Defaults to TZS; licences carry their bill currency on `feeCurrency`. */
    currency?: string;
    viewDetailsHandler: () => void;
    /**
     * Opens the control-number flow. The customer portal does not take card or mobile-push
     * payments directly — settlement happens against the control number.
     */
    onGenerateControlNumber?: () => void;
}

interface IRoomConfigCardProps {
    title: string;
    total: string;
    rate: string;
}


interface ApplicationDetailsCardProps {
    title: string;
    applicationId: string;
    /** Omitted when the application records no submission date. */
    submissionDate?: string;
    projectName: string;
    /** Omitted when the applicant gave no P.O. box. */
    projectPOBox?: string;
    /** Omitted when no contact e-mail is recorded against the applicant. */
    emailAddress?: string;
    /** Omitted entirely when the applicant gave no phone number. */
    phoneNumber?: string;
    /** Omitted when no physical location is recorded. */
    projectLocation?: string;
    /** Omitted entirely when the application has no recorded activities. */
    projectActivities?: string;
    /**
     * Licences derive this from `residences`; investments carry it on
     * `businessDetails.numberOfRooms`. Omitted when neither has any rooms.
     */
    numberOfRooms?: string | number;
    /**
     * Investment applications only — `describeTargetAudience` exists on the
     * investment `BusinessDetails` and on no part of a licence.
     */
    targetMarketDescription?: string;
    status: string;
    /** Omitted entirely when no bill/payment exists for the application yet. */
    paymentStatus?: string;
    applicationStatus: string;
}

/** A label/value pair that disappears rather than printing a placeholder. */
function DetailRow({ label, value }: { label: string; value?: string | number }) {
    if (value === undefined || value === null || value === "") return null;
    return (
        <div>
            <p className="text-gray-600 text-lg">{label}</p>
            <p className="text-sm text-gray-900 font-medium">{value}</p>
        </div>
    );
}

export default function ApplicationDetailsCard({
    title,
    applicationId,
    submissionDate,
    applicationStatus,
    projectName,
    projectPOBox,
    emailAddress,
    phoneNumber,
    projectLocation,
    projectActivities,
    numberOfRooms,
    targetMarketDescription,
    status,
    paymentStatus,
}: ApplicationDetailsCardProps) {
    return (
        <div className="bg-white p-6 rounded-lg space-y-6 w-[35%]">

            <div className="space-y-2">
                <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                <p className="text-gray-600">Application ID: {applicationId}</p>
                
                <div className="flex gap-2 mt-2">
                        <ExtendedBadge variant={statusVariant(status)} label={status} />
                        {paymentStatus && (
                            <ExtendedBadge variant={statusVariant(paymentStatus)} label={paymentStatus} />
                        )}
                </div>
                
                {submissionDate && (
                    <div className="flex items-center gap-2 text-gray-600">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span>Submitted on: {submissionDate}</span>
                    </div>
                )}
            </div>

            <div className="border-t pt-6 space-y-6">
                <h3 className="text-xl font-bold text-gray-900">Application Details</h3>
                
                <div className="space-y-1">
                    <p className="text-gray-600">Application Status</p>
                    <ExtendedBadge variant={statusVariant(applicationStatus)} label={applicationStatus} />
                </div>

                <div className="space-y-3">
                    <DetailRow label="Project Name" value={projectName} />
                    <DetailRow label="Project P.O. Box" value={projectPOBox} />
                    <DetailRow label="Email address" value={emailAddress} />
                    <DetailRow label="Phone Number" value={phoneNumber} />
                    <DetailRow label="Project Location" value={projectLocation} />
                    <DetailRow label="Project Activities" value={projectActivities} />
                    <DetailRow label="Number of Rooms" value={numberOfRooms} />
                    <DetailRow label="Target Market Description" value={targetMarketDescription} />
                </div>
            </div>
        </div>
    );
}



export const RoomConfigCard = ({ title, total, rate }:IRoomConfigCardProps) => {
    return (
        <div className="bg-white shadow-sm rounded-lg p-4 border border-gray-200">
            <h2 className="text-lg font-semibold text-brown-800 mb-4">{title}</h2>
            <div className="grid grid-cols-2 divide-x border p-1 rounded-md   text-sm text-gray-700">
                <div className='pe-6'>
                    <h3 className="font-light">Total</h3>
                    <p className="mt-1 text-sm font-semibold text-brown-700">{total}</p>
                </div>
                
                <div className='ps-6'>
                    <h3 className="font-light">Rate</h3>
                    <p className="mt-1 text-sm font-semibold text-brown-700">{rate}</p>
                </div>
            </div>
        </div>
    );
};


export const PaymentCard = ({
    reference,
    baseFee,
    managementFee,
    inspectionFee,
    totalFees,
    paymentDate,
    currency = "TZS",
    viewDetailsHandler,
    onGenerateControlNumber,
}: PaymentCardProps) => {
    const formatNumber = (value: string) => {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return num.toLocaleString();
    };

    const FeeRow = ({ label, value }: { label: string; value?: string }) => {
        if (value === undefined || value === "") return null;
        return (
            <div className="flex justify-between">
                <span>{label}</span>
                <span>{formatNumber(value)} {currency}</span>
            </div>
        );
    };

    const hasBreakdown = [baseFee, managementFee, inspectionFee, totalFees]
        .some((fee) => fee !== undefined && fee !== "");

    return (
        <div>
            <h1 className="my-2 text-lg font-semibold text-[#3C1E03]">Fees Calculation</h1>
            <div className="bg-white shadow-sm rounded-2xl p-4 ">
                <div className="flex justify-between items-start mb-4">
                    <span className="text-gray-500">To be paid on</span>
                    {reference && (
                        <span className="text-sm text-brown-800 font-medium">Ref: {reference}</span>
                    )}
                </div>
                {hasBreakdown ? (
                    <div className="space-y-2 text-gray-700 text-sm">
                        <FeeRow label="Base Fee" value={baseFee} />
                        <FeeRow label="Management Fee" value={managementFee} />
                        <FeeRow label="Inspection Fee" value={inspectionFee} />
                        {totalFees !== undefined && totalFees !== "" && (
                            <div className="border-t border-gray-300 pt-2 flex justify-between font-semibold text-gray-900">
                                <span>Total Fees</span>
                                <span>{formatNumber(totalFees)} {currency}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">
                        No fees have been calculated for this application yet.
                    </p>
                )}
                {onGenerateControlNumber && !paymentDate && (
                    <button
                        onClick={onGenerateControlNumber}
                        className="mt-3 w-full py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors"
                    >
                        Generate Control Number
                    </button>
                )}
                <div className="bg-[#f5f2f0] rounded-lg mt-4 p-3 flex justify-between items-center">
                    {/* <span className="text-sm text-gray-500">
                        {paymentDate
                            ? <>Paid on: <span className="text-brown-800 font-medium">{paymentDate}</span></>
                            : "Not paid yet"}
                    </span> */}
                    <button
                        onClick={viewDetailsHandler}
                        className="text-sm text-orange-500 font-semibold hover:underline flex items-center"
                    >
                        View Details <span className="ml-1">&rarr;</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
